import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import type { ManagerMeData } from '@/services/managers/me'
import { isAwaitingAccountActivation, useManagerMeApi } from '@/services/managers/me'

/**
 * Rotas onde não forçamos repatriar para onboarding (fluxo próprio ou pré-conta).
 * Ex.: usuário em "editar empresa" ou seleção de conta.
 */
const ONBOARDING_REDIRECT_EXEMPT_ROUTE_NAMES = new Set<string>([
  'edit-business',
  'select-account',
  'register-business',
  'callback',
  'swap-virtual-card',
  'swap-virtual-card-mobile',
])

function sameRouteLocation(
  to: RouteLocationNormalized,
  destination: RouteLocationRaw,
): boolean {
  if (typeof destination !== 'object' || destination === null || !('name' in destination)) {
    return false
  }
  if (typeof destination.name !== 'string' || to.name !== destination.name) {
    return false
  }
  if (destination.name === 'onboarding') {
    const destStep =
      destination.query && typeof destination.query === 'object' && 'step' in destination.query
        ? String(destination.query.step ?? '')
        : ''
    const toStep = typeof to.query.step === 'string' ? to.query.step : ''
    return destStep === toStep
  }
  return true
}

/**
 * Se o `/manager/me` exige conclusão de cadastro, retorna para onde o usuário deve ir.
 * Retorna `null` quando a rota atual já é a correta ou está isenta.
 */
export function resolveManagerOnboardingRedirect(
  to: RouteLocationNormalized,
  me: ManagerMeData,
): RouteLocationRaw | null {
  const { needsValidateData, needsPreApprovalDataConference, needsCompleteShareholder, isRejected } =
    useManagerMeApi()

  let required: RouteLocationRaw | null = null

  if (me.terms_accepted === false) {
    required = { name: 'onboarding', query: { step: '1' } }
  } else if (isRejected(me)) {
    required = { name: 'rejected' }
  } else if (isAwaitingAccountActivation(me.business_unit_status)) {
    required = { name: 'in-analysis' }
  } else if (needsValidateData(me.business_unit_status)) {
    required = { name: 'onboarding', query: { step: '2' } }
  } else if (needsPreApprovalDataConference(me)) {
    required = { name: 'onboarding', query: { step: '2' } }
  } else if (needsCompleteShareholder(me.shareholder_registration_status, me.business_unit_status)) {
    required = { name: 'onboarding', query: { step: '3' } }
  }

  if (!required) return null
  if (sameRouteLocation(to, required)) return null

  if (typeof to.name === 'string' && ONBOARDING_REDIRECT_EXEMPT_ROUTE_NAMES.has(to.name)) {
    return null
  }

  return required
}
